<template>
  <div class="digital-delivery-form">
    <form-builder
      ref="formBuilder"
      :model="digitalRecipient"
      :schema="schema"
      dense
      @field-changed="fieldChanged"
    >
      <template #email="{ label, value, rules }">
        <v-form
          ref="emailAddressForm"
          v-model="isEmailValid"
          lazy-validation
        >
          <v-text-field
            id="checkout-email-input"
            class="checkout-email-input"
            :class="checkoutEmailDisabledWithErrorClass"
            name="checkout-digital-email"
            outlined
            type="email"
            :aria-label="$t('checkout-recipient-email')"
            placeholder="your_email@email.com"
            maxlength="100"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            aria-invalid="false"
            :disabled="disableEmail"
            :value="value"
            :label="label"
            :rules="rules"
            persistent-hint
            :hint="emailHint"
            @input="fieldChanged('email', $event)"
            @paste.prevent
          />
        </v-form>
        <v-col
          v-if="!disableEmail"
          cols="12"
          class="pt-4 pb-1 px-0"
        >
          <v-text-field
            id="checkout-confirm-email-input"
            v-model="confirmationEmail"
            :label="$t('checkout-recipient-confirmation-email')"
            type="email"
            outlined
            name="checkout-digital-confirmation-email"
            :aria-label="$t('checkout-recipient-confirmation-email')"
            placeholder="your_email@email.com"
            maxlength="100"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            aria-invalid="false"
            :rules="confirmEmailRules"
            @paste.prevent
          />
        </v-col>
      </template>
    </form-builder>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import {
  GET_REWARD_LINK,
  GET_DIGITAL_RECIPIENT,
  GET_IS_PRINTABLE_RL
} from '@/store/get-types'
import { UPDATE_DIGITAL_RECIPIENT_DATA } from '@/store/action-types'
import { RL_EMPTY_UNLOCKED_EMAIL } from '../../plugins/split/ActiveSplits'
import { FormBuilder } from '@tangocard/mango'
import DigitalDeliveryFormSchema from '@/components/shared/forms/DigitalDeliveryFormSchema'
import FormValidationRules from '@/components/shared/forms/FormValidationRules'

export default {
  name: 'DigitalDeliveryForm',
  components: {
    FormBuilder
  },
  props: {
    formIsValid: {
      type: Boolean,
      required: false,
      default: true
    },
    validate: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      confirmationEmail: null,
      disableEmail: false,
      isEmailValid: true,
      schema: DigitalDeliveryFormSchema
    }
  },
  computed: {
    ...mapGetters({
      rewardLink: GET_REWARD_LINK,
      digitalRecipient: GET_DIGITAL_RECIPIENT,
      isPrintableRl: GET_IS_PRINTABLE_RL
    }),
    // should we populate an empty email
    autoPopulateEmptyEmail () {
      // Once we move this over to a customization field, that check can just replace the split check here
      return (
        !this.shouldLockEmail && this.$isSplitEnabled(RL_EMPTY_UNLOCKED_EMAIL)
      )
    },
    checkoutEmailDisabledWithErrorClass () {
      return this.disableEmail && !this.isEmailValid
        ? 'disabled-with-error'
        : null
    },
    shouldLockEmail () {
      // If the item says they cant override, then we lock it no matter what
      return !this.rewardLink.customization.allowRecipientEmailOverride
    },
    emailHint () {
      if (this.disableEmail) {
        return this.$t('checkout-email-disabled')
      } else if (this.autoPopulateEmptyEmail) {
        return this.$t('checkout-email-personal')
      } else {
        return ''
      }
    },
    confirmEmailRules () {
      return [
        FormValidationRules.required(this.$t('checkout-recipient-email-valid')),
        FormValidationRules.email(this.$t('checkout-recipient-email-valid')),
        FormValidationRules.matches(
          this.digitalRecipient.email,
          this.$t('checkout-recipient-confirmation-email-matches')
        )
      ]
    }
  },
  watch: {
    validate (val) {
      if (val) {
        this.validateForm()
      }
    }
  },
  async mounted () {
    const firstName = _.get(
      this.rewardLink,
      ['metadata', 'checkoutFirstName'],
      ''
    ).trim()
    const lastName = _.get(
      this.rewardLink,
      ['metadata', 'checkoutLastName'],
      ''
    ).trim()
    const email = _.get(
      this.rewardLink,
      ['metadata', 'checkoutEmail'],
      ''
    ).trim()

    // If the email is going to be locked, we dont even care about checking the autoPopulateEmptyEmail
    // since they need to first disable that for the RL for it to work
    if (this.shouldLockEmail) {
      // But, we cant lock it if there is no populated email
      if (email) {
        this.disableEmail = true
      }
      await this.setDigitalRecipientValues({ email, firstName, lastName })
    } else {
      // The email is unlocked, so if the autoPopulateEmptyEmail is true, lets populate with the empty email
      if (this.autoPopulateEmptyEmail) {
        await this.setDigitalRecipientValues({ firstName, lastName })
      } else {
        await this.setDigitalRecipientValues({ email, firstName, lastName })
      }
    }
  },
  methods: {
    ...mapActions({
      updateDigitalRecipient: UPDATE_DIGITAL_RECIPIENT_DATA
    }),
    validateForm () {
      const isEmailValid = this.$refs.emailAddressForm.validate()
      const isValid = this.$refs.formBuilder.validate() && isEmailValid
      this.$emit('update:formIsValid', isValid)
      this.$emit('update:validate', false)
    },
    fieldChanged (field, value) {
      this.updateDigitalRecipient({ [field]: value })
      this.validateForm()
    },
    async setDigitalRecipientValues ({ email, firstName, lastName }) {
      if (this.isPrintableRl) {
        await this.updateDigitalRecipient({
          email,
          firstName: '',
          lastName: ''
        })
      } else {
        await this.updateDigitalRecipient({ email, firstName, lastName })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles';
@import '~vuetify/src/components/VTextField/variables';

::v-deep .checkout-email-input.disabled-with-error {
  .v-label,
  input,
  .v-messages__message {
    color: map-get($red, 'lighten-1') !important;
  }

  fieldset {
    color: map-get($red, 'lighten-3') !important;
    border: $text-field-outlined-fieldset-border;
  }
}
</style>
